// Small shared UI primitives styled from the Clinical Precision system.
//
// Everything here is sized thumb-first: the default control is 44px tall on a phone and settles to
// desktop density from `lg` up, so a page written once works in both places without per-screen
// media queries. Press feedback is part of the primitive, not something each caller remembers.
import type { ButtonHTMLAttributes, ReactNode } from 'react';

type Variant = 'primary' | 'accent' | 'ghost' | 'subtle' | 'danger';
type Size = 'sm' | 'md' | 'lg';

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-primary text-primary-fg hover:opacity-90 active:opacity-95',
  accent: 'bg-accent text-accent-fg hover:opacity-90 active:opacity-95',
  ghost: 'border border-line bg-transparent text-ink hover:bg-surface-2',
  subtle: 'bg-surface-2 text-ink hover:bg-line',
  danger: 'bg-status-urgent-fg text-white hover:opacity-90',
};

const SIZES: Record<Size, string> = {
  sm: 'min-h-[2.25rem] px-3 py-1.5 text-sm',
  md: 'min-h-[2.75rem] px-4 py-2 text-[15px] lg:min-h-[2.25rem] lg:text-sm',
  lg: 'min-h-[3rem] px-5 py-3 text-base font-semibold',
};

export function Button({
  variant = 'primary',
  size = 'md',
  block = false,
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: Size;
  /** Full-width — the right default for a phone form's submit. */
  block?: boolean;
}) {
  const base =
    'tap inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium transition disabled:pointer-events-none disabled:opacity-50';
  return (
    <button
      className={`${base} ${SIZES[size]} ${VARIANTS[variant]} ${block ? 'w-full' : ''} ${className}`}
      {...props}
    />
  );
}

/**
 * A card is a container with elevation, not a default wrapper. `flat` drops the shadow for cards
 * that sit inside another card or in a dense list, so the page doesn't turn into stacked boxes.
 */
/**
 * A surface.
 *
 * Padded by default. It used not to be, and every caller had to remember `p-6` — which is the kind
 * of API that produces a card with text jammed against its border the moment somebody forgets.
 * A card that wraps a full-bleed table passes `p-0` to opt out, which reads as the deliberate
 * choice it is.
 */
export function Card({
  children,
  className = '',
  flat = false,
}: {
  children: ReactNode;
  className?: string;
  flat?: boolean;
}) {
  // A padding utility in className wins, so `p-0` and `p-4` both still work.
  const hasOwnPadding = /(^|\s)(p|px|py|pt|pb|pl|pr)-/.test(className);
  return (
    <div
      className={`rounded-lg border border-line bg-surface ${hasOwnPadding ? '' : 'p-6'} ${flat ? '' : 'shadow-level-1'} ${className}`}
    >
      {children}
    </div>
  );
}

export function PageHeader({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: string;
  /** Trailing control — a primary action, a filter, a date range. */
  action?: ReactNode;
}) {
  return (
    <header className="mb-5 flex items-end justify-between gap-3 lg:mb-6">
      <div className="min-w-0">
        <h1 className="text-[1.6rem] font-bold leading-tight tracking-tight text-ink lg:text-2xl lg:font-semibold">
          {title}
        </h1>
        {subtitle && <p className="mt-1 max-w-[65ch] text-sm text-muted">{subtitle}</p>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </header>
  );
}

export function Pill({
  children,
  tone = 'neutral',
}: {
  children: ReactNode;
  tone?: 'neutral' | 'good' | 'bad' | 'warn';
}) {
  const tones = {
    neutral: 'bg-status-pending-bg text-status-pending-fg',
    good: 'bg-status-progress-bg text-status-progress-fg',
    bad: 'bg-status-urgent-bg text-status-urgent-fg',
    warn: 'bg-status-warn-bg text-status-warn-fg',
  };
  return (
    <span className={`inline-block rounded-md px-2 py-0.5 text-xs font-medium ${tones[tone]}`}>
      {children}
    </span>
  );
}

/**
 * An empty list is a place to tell someone what to do next, not a blank panel. Give it the reason
 * the list is empty and, where there is one, the action that fills it.
 */
export function EmptyState({
  title,
  body,
  action,
  icon,
}: {
  title: string;
  body?: string;
  action?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className="rounded-lg border border-dashed border-line bg-surface/60 px-6 py-10 text-center">
      {icon && <div className="mx-auto mb-3 grid h-11 w-11 place-items-center rounded-full bg-surface-2 text-muted">{icon}</div>}
      <p className="font-semibold text-ink">{title}</p>
      {body && <p className="mx-auto mt-1 max-w-[42ch] text-sm text-muted">{body}</p>}
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  );
}
